import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Select, Store } from "@ngxs/store";
import { Observable, Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { AdvancedReportIndexState, AdvancedReportIndexStateModel, DeleteAdvancedReport, InitAdvancedReportIndex, LoadAdvancedReportIndex, NewAdvancedReport, PageAdvancedReportIndex, SortAdvancedReportIndex } from "./report-index.state";
import { PageEvent } from "@angular/material/paginator";
import { Sort } from "@angular/material/sort";
import { AdvancedReport } from "@core/data/types/advanced-report";
import { Dialog } from "@core/material";
import { AdvancedReportFormDialog, ReportFormDialogData } from "../report-dialogs/report-dialog/advanced-report-form.dialog";
import { AdvancedReportShareDialog, AdvancedReportShareDialogData } from "../report-dialogs/report-share-dialog/advanced-report-share.dialog";
import { FilterSort } from '@core/data';
import { FormControl, FormGroup } from '@angular/forms';

@Component({
    selector: 'pv-advanced-report-index-view',
    templateUrl: 'report-index.view.html',
    host: {
        class: 'pv-advanced-report-index-view'
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvancedReportIndexView implements OnInit, OnDestroy {

    @Select(AdvancedReportIndexState)
    state$: Observable<AdvancedReportIndexStateModel>;

    @Select(AdvancedReportIndexState.sort)
    sort$: Observable<FilterSort>;

    // sort form group
    orderControl = new FormControl('desc');
    columnControl = new FormControl('updatedAt');
    sortFormGroup = new FormGroup({
        column: this.columnControl,
        order: this.orderControl,
    });

    private _destroy$ = new Subject();

    constructor(
        private _store: Store,
        private _route: ActivatedRoute,
        private _dialog: Dialog,
    ) {}

    ngOnInit(): void {
        this._route.paramMap
            .pipe(takeUntil(this._destroy$))
            .subscribe(params => {
                this._store.dispatch(new InitAdvancedReportIndex(params.get('orgKey')))
            })

        // apply state changes to sort form
        this.sort$.pipe(takeUntil(this._destroy$))
            .subscribe(sort => this.sortFormGroup.patchValue(sort, { emitEvent: false }));

        // handle sort changes
        this.sortFormGroup.valueChanges
            .pipe(debounceTime(200), takeUntil(this._destroy$))
            .subscribe(val => {
                this._store.dispatch(new SortAdvancedReportIndex(val.column, val.order));
            });

        this.state$.pipe(takeUntil(this._destroy$))
            .subscribe(state => {
                if (!state.sortOptions.map(opt => opt.id).includes(this.columnControl.value)) {
                    this.columnControl.setValue('updatedAt')
                }
            });

    }

    paginate(page: PageEvent) {
        this._store.dispatch(new PageAdvancedReportIndex(page.pageIndex, page.pageSize));
    }

    sort(sort: Sort) {
        if (sort.direction) {
            this._store.dispatch(new SortAdvancedReportIndex(sort.active, sort.direction));
        } else {
            this._store.dispatch(new SortAdvancedReportIndex('updatedAt', 'desc'));
        }
    }

    toggleSortOrder(event: MouseEvent) {
        event.stopPropagation();
        this.orderControl.setValue(this.orderControl.value === 'asc' ? 'desc' : 'asc');
    }

    resetFilter() {
        console.log('reset filters')
    }

    reload() {
        this._store.dispatch(new LoadAdvancedReportIndex);
    }

    add() {
        this._store.dispatch(new NewAdvancedReport);
    }

    edit(report: AdvancedReport) {
        const data: ReportFormDialogData = {
            key: report.key,
            defaults: report
        }

        this._dialog.open(AdvancedReportFormDialog, {
            data,
            panelClass: 'reporting-theme'
        })
        .afterClosed()
        .subscribe(result => {
            if (!result) return;

            this.reload();
        })
    }

    shareReport(key: string, orgKey: string) {
        const data: AdvancedReportShareDialogData = {
            key,
            orgKey,
        };

        this._dialog.open(
            AdvancedReportShareDialog, {
                data,
                panelClass: 'reporting-theme',
                width: '480px',
                maxWidth: '95vw',
                autoFocus: false,
            }
        )
        .afterClosed()
        .pipe(takeUntil(this._destroy$))
        .subscribe(result => {
            if (!result) return;

            this.reload();
        })
    }

    deleteReport(report: AdvancedReport) {
        this._dialog.confirm(
            'Delete Report',
            `Are you sure you want to delete the '${report.label}' report?`,
            'Delete',
            'Cancel',
            true
        )
        .afterClosed()
        .subscribe(result => {
            if (!result) return;

            this._store.dispatch(new DeleteAdvancedReport(report.key));
        })

    }

    trackReport(index, report){
        return report.key;
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }
}